<template>
  <el-card>
    <div
        slot="header"
        class="clearfix">
      <span class="header-item">Cellular Usage</span>
      <span :class="{'ring-card__header-buttons': true, 'disable-html': loading }">

        <RefreshButton @click.native="fetch()" />
      </span>
    </div>
    <div
        v-loading="loading"
        class="card-content">
      <el-table
          :data="cellularUsage"
          class="device-auth-table"
          style="width: 100%">
        <el-table-column
            prop="id"
            width="150px"
            label="Device Id">
          <template slot-scope="scope">
            <router-link :to="{ name: 'Device', params: { customer_id: scope.row.ownerId, doorbot_id: scope.row.doorbotId }}">
              {{ scope.row.id }}
            </router-link>
          </template>
        </el-table-column>

        <el-table-column
            width="180px"
            label="Type">
          <template slot-scope="scope">
            <span>{{ scope.row.type }}</span>
          </template>
        </el-table-column>

        <el-table-column
            width="180px"
            prop="name"
            label="Name">
          <template slot-scope="scope">
            <span style="word-break: break-word">
              {{ scope.row.name }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
            min-width="200px"
            label="Location">
          <template slot-scope="scope">
            <span>{{ scope.row.locationID }}</span>
          </template>
        </el-table-column>
        <el-table-column
            label="Base GB">
          <template slot-scope="scope">
            <span>{{ scope.row.baseGB }}</span>
          </template>
        </el-table-column>

        <el-table-column
            label="Base used">
          <template slot-scope="scope">
            <span>{{ scope.row.baseGBUsed }}</span>
          </template>
        </el-table-column>

        <el-table-column
            min-width="90px"
            label="Base used%">
          <template slot-scope="scope">
            <span>{{ scope.row.basePercentUsed }}</span>
          </template>
        </el-table-column>

        <el-table-column
            width="90px"
            label="Extra data used">
          <template slot-scope="scope">
            <span>{{ scope.row.extraDataUsed }}</span>
          </template>
        </el-table-column>

        <el-table-column
            width="120px"
            label="Remaining days">
          <template slot-scope="scope">
            <span>{{ scope.row.remainingDays }}</span>
          </template>
        </el-table-column>

        <el-table-column
            width="70px"
            label="LTE">
          <template slot-scope="scope">
            <span>{{ scope.row.hasLtePlan }}</span>
          </template>
        </el-table-column>

        <el-table-column
            width="110px"
            label="LTE Overage Eligible">
          <template slot-scope="scope">
            <span>{{ scope.row.isLteOverageEligible }}</span>
          </template>
        </el-table-column>
      </el-table>
      <div class="cellular-usage-table__footer-pagination">
        <el-alert
            v-if="error.value"
            style="margin-bottom: 14px"
            :title="error.message"
            type="info"
            center
            show-icon />
      </div>
    </div>
  </el-card>
</template>

<script>
import { faCircle } from "@fortawesome/fontawesome-free-solid";
import { startCase, merge, get, forEach } from "lodash";
import RefreshButton from "@/containers/Customer/RefreshButton";

import { createNamespacedHelpers as h } from "vuex";

const { mapActions: accountActions } = h("db/account/location");

export default {
  name: "CellularUsage",
  components: {
    RefreshButton,
  },
  props: {
    customer: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      cellularUsage: [],
      error: {
        value: false,
        message: "Failed to load devices",
      },
    };
  },
  created() {
    this.fetch();
  },
  methods: {
    ...accountActions(["loadCellularData"]),
    bytesToGB(value) {
      return (value / 1024 ** 3).toFixed(2);
    },
    calculateRemainingDays(value) {
      var total_hours = this.$moment(value).diff(new Date(), "hours");
      var days = Math.floor(total_hours / 24);
      var hours = Math.floor(total_hours) % 24;
      return `${days} days and ${hours} hs.`;
    },
    fetch() {
      this.loading = true;
      let base_stations = this.customer.ring_devices.filter(function (device) {
        return device.type == "base_station_k1";
      });
      let locations = base_stations
        .map((device) => {
          return `locations[]=${device.location_id}`;
        })
        .join("&");

      if (locations.length != 0) {
        this.loadCellularData({
          customerID: this.customer.id,
          locations: locations,
        })
          .then((response) => {
            this.cellularUsage = [];
            forEach(base_stations, (device) => {
              var row = response.data[device.location_id] || {};
              if (Object.keys(row).length) {
                row.baseGB = this.bytesToGB(
                  row.meteringCycles[0].baseBytesAllowed
                );
                row.baseGBUsed = this.bytesToGB(
                  row.meteringCycles[0].baseBytesUsed
                );
                row.extraDataUsed = this.bytesToGB(
                  row.meteringCycles[0].extraBytesUsed
                );

                row.basePercentUsed = row.meteringCycles[0].basePercentUsed;
                row.remainingDays = this.calculateRemainingDays(
                  row.meteringCycles[0].cycleEnd
                );
              } else {
                row.baseGB = "-";
                row.baseGBUsed = "-";
                row.extraDataUsed = "-";
                row.baseGBUsage = "-";
                row.remainingDays = "-";
                row.hasLtePlan = "false";
                row.isLteOverageEligible = "false";
              }
              row.type = device.type;
              row.name = device.name;
              row.id = device.mac_address;
              row.locationID = device.location_id;
              row.ownerId = device.owner_id;
              row.doorbotId = device.doorbot_id;
              this.cellularUsage.push(row);
            });
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this.loading = false;
      }
    },
    startCase: startCase,
  },
};
</script>

<style lang="scss">
.cellular-usage-table {
  .authorization-id {
    display: block;
    color: $--color-curious-blue;
  }
  .device-name {
    color: $--color-silver;
  }
  .lte_enabled__icon {
    color: transparentize($--color-fun-green, 0.6);
  }
}

.cellular-usage-table__footer-pagination {
  margin: 40px 0 20px;
}
.el-table.device-auth-table th > .cell {
  word-break: break-word;
}
</style>
